/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input, ViewChild } from '@angular/core';
import { ButtonService, DetailAbstract, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { LicenseKey } from 'app/common/constants/license-key';
import { type CIFSShare, type FileSystem } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { AddExtensionFilteringComponent } from '../../extension-filtering/add-extension-filtering/add-extension-filtering.component';
import { ExtensionFilteringComponent } from '../../extension-filtering/extension-filtering.component';
import { ShareMoTypeE } from '../../extension-filtering/extension-filtering-str.service';
import { AddIpWhiteListComponent } from '../../ip-white-list/add-ip-white-list/add-ip-white-list.component';
import { ShareTypeE } from '../../share.enum';
import { CifsShareStrService } from '../cifs-share-str.service';
import { AddUserOrGroupComponent } from '../client-cifs-share/add-user-or-group/add-user-or-group.component';
import { ClientCifsShareComponent } from '../client-cifs-share/client-cifs-share.component';
import { CreateCifsShareComponent } from '../create-cifs-share/create-cifs-share.component';
import { DeleteCifsShareComponent } from '../delete-cifs-share/delete-cifs-share.component';
import {
    AddHomedirRuleCifsShareComponent,
} from '../homedir-rule-cifs-share/add-homedir-rule-cifs-share/add-homedir-rule-cifs-share.component';
import { HomedirRuleCifsShareComponent } from '../homedir-rule-cifs-share/homedir-rule-cifs-share.component';
import { ModifyCifsShareComponent } from '../modify-cifs-share/modify-cifs-share.component';
import { CifsShareService } from './../cifs-share.service';

@Component({
    selector: 'app-detail-cifs-share',
    templateUrl: './detail-cifs-share.component.html',
    styleUrls: ['./detail-cifs-share.component.scss'],
})
export class DetailCifsShareComponent extends DetailAbstract<CIFSShare> implements OnInit {
    @Input() entry: FileSystemEntryE;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem: FileSystem;
    @ViewChild('clientDt') clientDt: ClientCifsShareComponent;
    @ViewChild('homedirRuleDt') homedirRuleDt: HomedirRuleCifsShareComponent;
    @ViewChild('extensionFilteringDt') extensionFilteringDt: ExtensionFilteringComponent;

    i18n = this.str.i18n;
    isEn = this.i18nService.language === 'en';
    UNPLACEHOLDER = UNPLACEHOLDER;
    activeTab: string = this.i18n.title.permissionList;
    enableShowSnapOpts = this.str.enableShowSnapOpts;
    ShareTypeE = ShareTypeE;
    ShareMoTypeE = ShareMoTypeE;
    fileModeOpts = this.str.fileModeOpts;
    SWITCH = SWITCH;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    /**
     * 侧滑框宽度
     */
    standardWidth = this.isEn ? '9rem' : '8rem';
    slideWindowWidth = this.isSimpleMode ? '6.08rem' : this.standardWidth;

    createAsBtn: Button;
    addHomedirRuleBtn: Button;

    ProductConfig = ProductConfig;

    constructor(
        injector: Injector,
        public str: CifsShareStrService,
        private buttonService: ButtonService,
        private cifsService: CifsShareService,
        public utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
    }

    initOperationBtns(): void {
        this.createAsBtn = this.buttonService.create({
            id: 'create-as-detial-cifs-share',
            isShow: () => !SWITCH.IS_SIMPLE_MODE,
            label: this.i18n.btn.createAsBtn,
            permission: ['share_W'],
            license: [LicenseKey.NAS],
            disabledTips: this.i18n.tip.createAsHomedir,
            disabled: this.data.subType === ShareTypeE.Homedir,
            command: () => {
                this.create();
            },
        });
        this.addHomedirRuleBtn = this.buttonService.create({
            id: 'add-homedir-mapping-rule',
            isShow: () => !SWITCH.IS_SIMPLE_MODE,
            label: this.i18n.btn.addHomedirBtn,
            permission: ['share_W'],
            depPermission: ['file_system_R'],
            disabledTips: this.i18n.tip.addMapRule,
            disabled: this.data.subType !== ShareTypeE.Homedir,
            command: () => {
                this.addMappingRule(this.data);
            },
        });
        this.operationBtns = [
            this.createAsBtn,
            this.buttonService.create({
                id: 'add-client-detial-cifs-share',
                label: this.i18n.btn.addClient,
                theme: 'primary',
                permission: ['share_W'],
                depPermission: ['task_W'],
                command: () => {
                    this.addClient();
                },
            }),
            this.addHomedirRuleBtn,
            this.buttonService.create({
                id: 'add-extension-filerting',
                isShow: () => !SWITCH.IS_SIMPLE_MODE && !ProductConfig.isHideCifsFileFiltering,
                label: this.i18n.btn.addExtensionFilteringBtn,
                permission: ['share_W'],
                command: () => {
                    this.addExtensionFiltering();
                },
            }),
            this.buttonService.create({
                id: 'add-ip-white-list',
                isShow: () => !SWITCH.IS_SIMPLE_MODE,
                label: this.i18n.btn.addIpWhiteListBtn,
                permission: ['share_W'],
                command: () => {
                    this.addIpWhiteList();
                },
            }),
            { separator: true },
            this.buttonService.create({
                id: 'modify-cifs-share',
                label: this.i18n.btn.modifyBtn,
                permission: ['share_W'],
                command: () => {
                    this.modify();
                },
            }),
            this.buttonService.create({
                id: 'delete-cifs-share',
                label: this.i18n.btn.deleteBtn,
                permission: ['share_W'],
                command: () => {
                    this.delete();
                },
            }),
        ];
    }

    /**
     * 获取CIFS共享
     */
    getData(): Observable<any> {
        return this.cifsService.get(this.data.ID, this.data.vstoreId)
            .pipe(
                map(({ data }) => data),
            );
    }

    onDataUpdate(data: CIFSShare, isPolling: boolean): void {
        if (!isPolling) {
            if (this.createAsBtn && this.addHomedirRuleBtn) {
                this.createAsBtn.disabled = data.subType === ShareTypeE.Homedir;
                this.addHomedirRuleBtn.disabled = data.subType !== ShareTypeE.Homedir;
            }
        }
    }

    /**
     * 修改CIFS共享
     */
    modify(): void {
        this.doAction(ModifyCifsShareComponent, {
            data: this.data,
            entry: this.entry,
        });
    }

    /**
     * 删除CIFS共享
     */
    delete(): void {
        this.doAction(DeleteCifsShareComponent, { dataList: [this.data] });
    }

    create(): void {
        // 如果传入了文件系统，就传文件系统本身的租户ID
        const { vstoreId, vstoreName } = this.data;
        this.doAction(CreateCifsShareComponent, {
            vstoreId,
            vstoreName,
            createAsShare: this.data,
            supportSmartGui: false,
        });
    }

    addClient(): void {
        const selectedVstore = {
            label: this.data.vstoreName,
            value: this.data.vstoreId,
        };
        this.doAction(AddUserOrGroupComponent, {
            cifsShare: this.data,
            selectedVstore,
            entry: this.entry,
            fileSystem: this.fileSystem,
            onClose: (closeArgs) => {
                // 如果成功发起添加任务，此时要自动跳转到权限的页签
                if (closeArgs.switchToPermissionList) {
                    this.activeTab = this.i18n.title.permissionList;
                }
            },
        });
    }

    addMappingRule(data: CIFSShare): void {
        this.doAction(AddHomedirRuleCifsShareComponent, {
            cifsShare: data,
            vstoreId: data.vstoreId,
            vstoreName: data.vstoreName,
            onClose: (closeArgs) => {
                // 如果创建完成，这里变为刷新，此时要自动跳转到映射规则的页签
                if (closeArgs.refresh) {
                    this.activeTab = this.i18n.title.mappingRule;
                    if (this.homedirRuleDt) {
                        this.homedirRuleDt.refresh();
                    }
                }
            },
        });
    }

    addExtensionFiltering(): void {
        this.doAction(AddExtensionFilteringComponent, {
            share: this.data,
            shareType: ShareMoTypeE.CIFS,
            onClose: (closeArgs) => {
                // 如果创建完成，刷新表格
                if (closeArgs.refresh) {
                    if (this.extensionFilteringDt) {
                        this.extensionFilteringDt.refresh();
                    }
                }
            },
        });
    }

    addIpWhiteList(): void {
        this.doAction(AddIpWhiteListComponent, {
            cifsData: this.data,
        });
    }
}
